import { Stack } from '@chronosphereio/chrono-ui';
import { ContentCopy, DeleteOutline as DeleteIcon, PencilOutline as PencilIcon } from 'mdi-material-ui';
import { memo } from 'react';
import { ARIA_LABEL_TEXT, DASHBOARD_TOOLTIP_TEXT } from '../../constants';
import { SplitModeOptions, useIsSplitModeEnabled } from '../../context';
import { ActionButton } from '../ActionButton';

// Shrink the copy icon to look more consistent with the other icons
export const CONTENT_COPY_ICON_SCALE = 0.9;

export interface DashboardEditActionsProps {
  title: string;
  editHandlers: {
    onEditPanelClick?: () => void;
    onDuplicatePanelClick?: () => void;
    onDeletePanelClick?: () => void;
  };
}

/**
 * Panel-level actions in edit mode
 */
export function PanelEditActions(props: DashboardEditActionsProps) {
  const isSplitModeEnabled = useIsSplitModeEnabled();
  if (isSplitModeEnabled) {
    return (
      <SplitModeOptions>
        {({ isMainPanelGroup }) => <PanelEditActionsContent {...props} isDynamicPanel={!isMainPanelGroup} />}
      </SplitModeOptions>
    );
  }
  return <PanelEditActionsContent {...props} />;
}

interface PanelEditActionsContentProps extends DashboardEditActionsProps {
  isDynamicPanel?: boolean;
}

/**
 * Action content, differs when panel group is dynamic.
 */
const PanelEditActionsContent = memo(function PanelEditActionsContent(props: PanelEditActionsContentProps) {
  const { title, editHandlers, isDynamicPanel } = props;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {editHandlers.onEditPanelClick && (
        <ActionButton
          label={ARIA_LABEL_TEXT.editPanel(title)}
          helpText={
            isDynamicPanel === true ? DASHBOARD_TOOLTIP_TEXT.editDynamicPanel : DASHBOARD_TOOLTIP_TEXT.editPanel
          }
          onClick={editHandlers.onEditPanelClick}
          size="small"
          icon={<PencilIcon />}
        />
      )}
      {!isDynamicPanel && (
        <>
          {editHandlers.onDuplicatePanelClick && (
            <ActionButton
              label={ARIA_LABEL_TEXT.duplicatePanel(title)}
              helpText={DASHBOARD_TOOLTIP_TEXT.duplicatePanel}
              onClick={editHandlers.onDuplicatePanelClick}
              size="small"
              icon={<ContentCopy sx={{ transform: `scale(${CONTENT_COPY_ICON_SCALE})` }} />}
            />
          )}
          {editHandlers.onDeletePanelClick && (
            <ActionButton
              label={ARIA_LABEL_TEXT.deletePanel(title)}
              helpText={DASHBOARD_TOOLTIP_TEXT.deletePanel}
              onClick={editHandlers.onDeletePanelClick}
              size="small"
              icon={<DeleteIcon />}
            />
          )}
        </>
      )}
    </Stack>
  );
});
